import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ErrorAlert,
  Input,
} from '@react/components';
import { ListLegalEntities } from '@core/api/endpoints/legalentity/list_legal_entities';
import { ListProjects } from '@core/api/endpoints/project/list_projects';
import type { DocumentTemplate } from '@core/types/document';
import type { LegalEntity } from '@core/types/legalentity';
import type { Project } from '@core/types/project';
import type { Tag } from '@core/types/tag';
import { EntityCombobox } from '@/components/document/EntityCombobox';
import { TagPicker } from '@/components/TagPicker';
import { assignTagsToEntity } from '@/features/task/logic';
import {
  docTypeLabel,
  extractTemplateVars,
  missingRequiredVariables,
  previewTemplate,
  suggestVariables,
  todayISODate,
  unresolvedPlaceholders,
  type AutofillSource,
} from '@/features/document/templateAutofill';

type WizardStep = 0 | 1 | 2 | 3;

const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export const DocumentFromTemplatePage = observer(() => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const store = useDocument();
  const { t } = useI18n();
  const [step, setStep] = useState<WizardStep>(0);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [customer, setCustomer] = useState<LegalEntity | null>(null);
  const [manualProject, setManualProject] = useState('');
  const [manualCustomer, setManualCustomer] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [contextLoaded, setContextLoaded] = useState(false);
  const [contextError, setContextError] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [sources, setSources] = useState<Record<string, AutofillSource>>({});
  const [editedKeys, setEditedKeys] = useState<Set<string>>(new Set());
  const [titleOverride, setTitleOverride] = useState('');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [validationVisible, setValidationVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const submittingRef = useRef(false);
  const [today] = useState(todayISODate);

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    setLoadError(null);
    try {
      const response = await store.ListDocumentTemplates({ pagination: { limit: 100, cursor: '', direction: 'forward' } });
      const items = response.templates?.templates ?? [];
      setTemplates(items);
      setTemplate((current) => current ?? items.find((item) => item.entity?.id === templateId) ?? null);
    } catch (error) {
      setLoadError(error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [store, templateId]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const loadContext = useCallback(async () => {
    setContextLoading(true);
    setContextError(false);
    const [projectResult, legalEntityResult] = await Promise.allSettled([
      ListProjects({ pagination: { limit: 100, cursor: '', direction: 'forward' } }),
      ListLegalEntities({ limit: 100 }),
    ]);
    if (projectResult.status === 'fulfilled') {
      setProjects(projectResult.value.projects?.projects ?? []);
    }
    if (legalEntityResult.status === 'fulfilled') {
      setLegalEntities(legalEntityResult.value.items ?? []);
    }
    setContextError(projectResult.status === 'rejected' || legalEntityResult.status === 'rejected');
    setContextLoaded(true);
    setContextLoading(false);
  }, []);

  useEffect(() => {
    if (step === 1 && !contextLoaded && !contextLoading) void loadContext();
  }, [step, contextLoaded, contextLoading, loadContext]);

  const variables = useMemo(() => extractTemplateVars(template), [template]);
  const suggestions = useMemo(
    () => suggestVariables(variables, customer, project, today, { customer: manualCustomer, object: manualProject }),
    [variables, customer, project, today, manualCustomer, manualProject],
  );

  useEffect(() => {
    setValues((current) => {
      const next = { ...current };
      variables.forEach((variable) => {
        if (!editedKeys.has(variable.key)) next[variable.key] = suggestions[variable.key]?.value ?? '';
      });
      return next;
    });
    setSources((current) => {
      const next = { ...current };
      variables.forEach((variable) => {
        if (!editedKeys.has(variable.key)) next[variable.key] = suggestions[variable.key]?.source ?? null;
      });
      return next;
    });
  }, [variables, suggestions, editedKeys]);

  const variableValues = useMemo(
    () => Object.fromEntries(variables.map((variable) => [variable.key, values[variable.key] ?? ''])),
    [variables, values],
  );
  const generatedPreview = useMemo(() => previewTemplate(template, variableValues), [template, variableValues]);
  const preview = { ...generatedPreview, title: titleOverride.trim() || generatedPreview.title };
  const missingRequired = missingRequiredVariables(variables, variableValues);
  const unresolved = unresolvedPlaceholders(preview);
  const filteredTemplates = templates.filter((item) => {
    const query = templateSearch.trim().toLowerCase();
    return !query || item.name.toLowerCase().includes(query) || item.description.toLowerCase().includes(query);
  });
  const steps = [
    t('document.wizard.stepTemplate'),
    t('document.wizard.stepContext'),
    t('document.wizard.stepVariables'),
    t('document.wizard.stepPreview'),
  ];

  const selectTemplate = (next: DocumentTemplate) => {
    if (!next.is_active) return;
    setTemplate(next);
    setValues({});
    setSources({});
    setEditedKeys(new Set());
    setTitleOverride('');
    setValidationVisible(false);
    setSubmitError(null);
  };

  const changeVariable = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setEditedKeys((current) => new Set(current).add(key));
  };

  const goNext = () => {
    if (step === 0 && !template) return;
    if (step === 2 && missingRequired.length > 0) {
      setValidationVisible(true);
      return;
    }
    setValidationVisible(false);
    setStep((current) => Math.min(3, current + 1) as WizardStep);
  };

  const createDocument = async () => {
    const scopeEntity = project?.entity ?? customer?.entity ?? template?.scope_entity;
    if (
      submittingRef.current ||
      !template?.entity?.id ||
      !template.is_active ||
      !preview.title.trim() ||
      missingRequired.length > 0
    ) {
      setValidationVisible(true);
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await store.InstantiateDocumentFromTemplate(template.entity.id, {
        template: template.entity,
        title_override: preview.title.trim(),
        scope_entity: scopeEntity,
        variable_values: variableValues,
      });
      const documentId = response.document?.entity?.id;
      if (!documentId) throw new Error(t('document.wizard.createMissingId'));
      if (selectedTags.length > 0) {
        try {
          await assignTagsToEntity(selectedTags, documentId, 'document');
        } catch (error) {
          console.warn('[DocumentFromTemplatePage] Document created but additional tags were not assigned', error);
        }
      }
      navigate(`/documents/${documentId}`);
    } catch (error) {
      setSubmitError(error);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const sourceLabel = (source: AutofillSource, edited: boolean) => {
    if (edited) return t('document.wizard.sourceManual');
    if (!source) return '';
    const keys: Record<Exclude<AutofillSource, null>, string> = {
      customer: 'document.wizard.sourceCustomer',
      object: 'document.wizard.sourceObject',
      default: 'document.wizard.sourceDefault',
      date: 'document.wizard.sourceDate',
    };
    return t('document.wizard.filledFrom', { source: t(keys[source]) });
  };

  if (isLoadingTemplates && templates.length === 0) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6" aria-busy="true">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-96 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <h1 className="sr-only">{t('document.wizard.title')}</h1>
      <ol className="mb-5 flex items-center gap-1 overflow-x-auto" aria-label={t('document.wizard.title')}>
        {steps.map((label, index) => (
          <li key={label} className="flex min-w-0 flex-1 items-center gap-1">
            <button
              type="button"
              disabled={index > step}
              onClick={() => index < step && setStep(index as WizardStep)}
              aria-current={index === step ? 'step' : undefined}
              className={`flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-xs disabled:cursor-default ${
                index === step ? 'text-foreground' : index < step ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-full border tabular-nums ${
                index === step ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
              }`}
              >
                {index + 1}
              </span>
              <span className="truncate">{label}</span>
            </button>
            {index < steps.length - 1 && <span className="h-px min-w-2 flex-1 bg-border" aria-hidden="true" />}
          </li>
        ))}
      </ol>

      {loadError != null && (
        <div className="mb-4 space-y-2">
          <ErrorAlert error={loadError} dismissible onDismiss={() => setLoadError(null)} />
          <Button type="button" variant="secondary" size="sm" onClick={() => void loadTemplates()}>
            {t('errors.tryAgain')}
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{steps[step]}</CardTitle>
          <CardDescription>
            {step === 0 && t('document.wizard.templateHint')}
            {step === 1 && t('document.wizard.contextHint')}
            {step === 2 && t('document.wizard.variablesHint')}
            {step === 3 && t('document.wizard.previewHint')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {step === 0 && (
            <>
              {template && (
                <div className="rounded-md border border-border bg-muted/30 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-medium text-foreground">{template.name}</h2>
                    <Badge variant={template.is_active ? 'success' : 'secondary'} size="sm">
                      {template.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                    {template.document_type && <Badge variant="secondary" size="sm">{docTypeLabel(t, template.document_type)}</Badge>}
                  </div>
                  {template.description && <p className="mt-1 text-sm text-muted-foreground">{template.description}</p>}
                </div>
              )}
              <Input
                value={templateSearch}
                onChange={(event) => setTemplateSearch(event.target.value)}
                placeholder={t('document.wizard.templateSearchPlaceholder')}
                aria-label={t('document.wizard.templateSearchPlaceholder')}
              />
              {!isLoadingTemplates && filteredTemplates.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  {templates.length === 0 ? t('document.wizard.noTemplates') : t('common.noResults')}
                </div>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {filteredTemplates.map((item) => (
                    <li key={item.entity?.id ?? item.name}>
                      <button
                        type="button"
                        disabled={!item.is_active}
                        onClick={() => selectTemplate(item)}
                        className={`w-full px-4 py-3 text-left hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50 ${
                          template?.entity?.id === item.entity?.id ? 'bg-muted/50' : ''
                        }`}
                      >
                        <span className="block text-sm font-medium text-foreground">{item.name}</span>
                        {item.description && <span className="mt-0.5 block text-xs text-muted-foreground">{item.description}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {step === 1 && (
            <>
              {contextError && (
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                  <span>{t('document.wizard.contextLoadError')}</span>
                  <Button type="button" variant="secondary" size="sm" onClick={() => void loadContext()}>
                    {t('errors.tryAgain')}
                  </Button>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <EntityCombobox
                  id="template-project"
                  label={t('document.wizard.objectLabel')}
                  placeholder={t('document.wizard.objectPlaceholder')}
                  items={projects.filter((item) => item.entity?.id).map((item) => ({
                    id: item.entity!.id,
                    title: item.name,
                    subtitle: item.key,
                  }))}
                  loading={contextLoading}
                  value={project?.entity?.id ? { id: project.entity.id, title: project.name, subtitle: project.key } : null}
                  onSelect={(item) => {
                    setProject(item ? projects.find((candidate) => candidate.entity?.id === item.id) ?? null : null);
                    if (item) setManualProject('');
                  }}
                  manualText={manualProject}
                  onManualTextChange={(text) => {
                    setManualProject(text);
                    setProject(null);
                  }}
                  emptyText={t('document.wizard.objectEmpty')}
                  manualActionText={t('document.wizard.manualAction')}
                  manualPlaceholder={t('document.wizard.manualObjectPlaceholder')}
                  manualHint={t('document.wizard.manualHint')}
                />
                <EntityCombobox
                  id="template-customer"
                  label={t('document.wizard.customerLabel')}
                  placeholder={t('document.wizard.customerPlaceholder')}
                  items={legalEntities.filter((item) => item.entity?.id).map((item) => ({
                    id: item.entity!.id,
                    title: item.short_name || item.full_name,
                    subtitle: item.primary_tax_id,
                  }))}
                  loading={contextLoading}
                  value={customer?.entity?.id ? {
                    id: customer.entity.id,
                    title: customer.short_name || customer.full_name,
                    subtitle: customer.primary_tax_id,
                  } : null}
                  onSelect={(item) => {
                    setCustomer(item ? legalEntities.find((candidate) => candidate.entity?.id === item.id) ?? null : null);
                    if (item) setManualCustomer('');
                  }}
                  manualText={manualCustomer}
                  onManualTextChange={(text) => {
                    setManualCustomer(text);
                    setCustomer(null);
                  }}
                  emptyText={t('document.wizard.customerEmpty')}
                  manualActionText={t('document.wizard.manualAction')}
                  manualPlaceholder={t('document.wizard.manualCustomerPlaceholder')}
                  manualHint={t('document.wizard.manualHint')}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('document.wizard.scopeHint')}</p>
            </>
          )}

          {step === 2 && (
            variables.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('document.wizard.noVariables')}</p>
            ) : (
              <div className="space-y-4">
                {validationVisible && missingRequired.length > 0 && (
                  <p role="alert" className="text-sm text-destructive">{t('document.wizard.requiredMissing')}</p>
                )}
                {variables.map((variable) => {
                  const missing = validationVisible && missingRequired.some((item) => item.key === variable.key);
                  const source = sourceLabel(sources[variable.key], editedKeys.has(variable.key));
                  return (
                    <div key={variable.key}>
                      <label htmlFor={`template-var-${variable.key}`} className="mb-1 block text-sm font-medium text-foreground">
                        {variable.label}
                        {variable.required && <span className="ml-1 text-destructive" aria-label={t('common.required')}>*</span>}
                      </label>
                      <input
                        id={`template-var-${variable.key}`}
                        type="text"
                        value={values[variable.key] ?? ''}
                        onChange={(event) => changeVariable(variable.key, event.target.value)}
                        aria-invalid={missing}
                        aria-describedby={`template-var-${variable.key}-hint`}
                        required={variable.required}
                        className={`${inputClass} ${missing ? 'border-destructive focus:ring-destructive' : ''}`}
                      />
                      <div id={`template-var-${variable.key}-hint`} className="mt-1 min-h-4 text-xs text-muted-foreground">
                        {missing ? t('document.wizard.fieldRequired') : source || variable.description}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {step === 3 && (
            <>
              {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
              <div>
                <label htmlFor="template-title" className="mb-1 block text-sm font-medium text-foreground">
                  {t('document.wizard.titleLabel')}
                </label>
                <input
                  id="template-title"
                  type="text"
                  value={preview.title}
                  onChange={(event) => setTitleOverride(event.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              {(validationVisible && (!preview.title.trim() || missingRequired.length > 0)) && (
                <p role="alert" className="text-sm text-destructive">
                  {!preview.title.trim() ? t('document.wizard.titleRequired') : t('document.wizard.requiredMissing')}
                </p>
              )}
              <section aria-labelledby="template-preview-heading" className="rounded-md border border-border">
                <h2 id="template-preview-heading" className="border-b border-border px-4 py-3 text-sm font-medium text-foreground">
                  {t('document.wizard.previewTitle')}
                </h2>
                <div className="space-y-4 px-4 py-4 text-sm leading-relaxed text-foreground">
                  <p className="text-lg font-semibold">{preview.title || t('common.untitled')}</p>
                  {preview.content && <p className="whitespace-pre-wrap">{preview.content}</p>}
                  {preview.blocks.map((block, index) => <p key={`${index}-${block}`} className="whitespace-pre-wrap">{block}</p>)}
                  {!preview.content && preview.blocks.length === 0 && (
                    <p className="text-muted-foreground">{t('document.wizard.emptyPreview')}</p>
                  )}
                </div>
              </section>
              {unresolved.length > 0 && (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" role="status">
                  <p className="font-medium text-warning">{t('document.wizard.unresolvedTitle')}</p>
                  <p className="mt-1 text-muted-foreground">{t('document.wizard.unresolvedHint')}</p>
                  <ul className="mt-2 flex flex-wrap gap-1">
                    {unresolved.map((key) => <li key={key}><Badge variant="warning" size="sm">{`{{${key}}}`}</Badge></li>)}
                  </ul>
                </div>
              )}
              <div>
                <span className="mb-1 block text-sm font-medium text-foreground">{t('task.field.tags')}</span>
                {(template?.default_tags?.length ?? 0) > 0 ? (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {(template?.default_tags ?? []).map((tag) => <Badge key={tag} variant="secondary" size="sm">{tag}</Badge>)}
                  </div>
                ) : null}
                <TagPicker value={selectedTags} onChange={setSelectedTags} placeholder={t('tag.selectTags')} />
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="flex flex-wrap items-center gap-2">
          <Link to="/documents/templates" className="mr-auto">
            <Button type="button" variant="ghost" disabled={isSubmitting}>{t('common.cancel')}</Button>
          </Link>
          {step > 0 && (
            <Button type="button" variant="secondary" disabled={isSubmitting} onClick={() => setStep((current) => Math.max(0, current - 1) as WizardStep)}>
              {t('common.back')}
            </Button>
          )}
          {step < 3 ? (
            <Button type="button" disabled={step === 0 && !template} onClick={goNext}>{t('common.next')}</Button>
          ) : (
            <Button
              type="button"
              isLoading={isSubmitting}
              disabled={isSubmitting || !template?.is_active}
              onClick={() => void createDocument()}
            >
              {isSubmitting ? t('common.creating') : t('document.wizard.createDocument')}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
});

export default DocumentFromTemplatePage;
